/** AI doubt-clearing tutor: ask a question in any language, get the answer in
 *  that same language.
 *
 *  Every exchange is saved as a THREAD. That is what lets a learner follow
 *  up ("why?" means "why THAT?") and what the History screen reads back —
 *  the client reported both as missing: the Ask box "did not have an option
 *  to go further", and there was no record of anything asked. */
import type { AiModel } from "./exams";
import { request } from "./http";

export interface DoubtAnswer {
  configured: boolean;
  answer: string;
  /** Pass back as `threadId` to continue this conversation. */
  thread_id: string | null;
}

export interface DoubtMessage {
  role: "learner" | "tutor";
  text: string;
  at?: string | null;
}

export interface DoubtThread {
  id: string;
  title: string;
  context?: string | null;
  messages: DoubtMessage[];
  created_at: string;
  last_message_at?: string | null;
}

export interface DoubtThreadSummary {
  id: string;
  title: string;
  message_count: number;
  created_at: string;
  last_message_at?: string | null;
}

export function askDoubt(
  question: string, context?: string, threadId?: string | null,
  model?: string | null,
): Promise<DoubtAnswer> {
  return request("/tutor/doubt", {
    method: "POST",
    body: {
      question, context, thread_id: threadId ?? null,
      model: model ?? null,
    },
  });
}

/** Which models this deployment can put a doubt to, and what each costs.
 *  Never a key. */
export function doubtModels(): Promise<{
  models: AiModel[];
  active_model?: string | null;
}> {
  return request("/tutor/models");
}

export function listDoubts(): Promise<DoubtThreadSummary[]> {
  return request("/tutor/doubts");
}

export function readDoubt(id: string): Promise<DoubtThread> {
  return request(`/tutor/doubts/${id}`);
}

export function deleteDoubt(id: string): Promise<void> {
  return request(`/tutor/doubts/${id}`, { method: "DELETE" });
}
